/**
 * Pure builders for affiliate analytics events. Kept in the lib so both
 * `apps/mobile` (PostHog client) and any future API-side emitter share
 * one canonical property shape. See ADR-0064.
 *
 * Two events with identical property shapes so PostHog can compute CTR
 * by joining them on `surface` / `product` / `entity_kind`:
 *   - `affiliate_impression` — fires once when a CTA becomes visible.
 *   - `affiliate_click` — fires when the user taps a CTA.
 */

export type AffiliateEntityKind = 'trip' | 'stay' | 'journey' | 'place';
export type AffiliateSurface =
  | 'trip_widget'
  | 'stay_row'
  | 'journey_row'
  | 'place_detail'
  | 'day_banner'
  | 'day_ad'
  | 'add_choice';
export type AffiliateProduct =
  | 'hotel'
  | 'flight'
  | 'tours'
  | 'esim'
  | 'car_rental'
  | 'transfer'
  | 'tickets'
  | 'reserve'
  | 'train'
  | 'bus'
  | 'ferry';

export interface BuildAffiliateEventInput {
  provider: string;
  surface: AffiliateSurface;
  product: AffiliateProduct;
  entityKind: AffiliateEntityKind;
  entityId: string;
  tripId: string;
  /** Free-form context — flight mode + IATA pair, destination name, etc. */
  context?: string | undefined;
}

/** @deprecated Use `BuildAffiliateEventInput`. Kept as an alias for back-compat. */
export type BuildAffiliateClickEventInput = BuildAffiliateEventInput;

type AffiliateEventProperties = {
  provider: string;
  surface: AffiliateSurface;
  product: AffiliateProduct;
  entity_kind: AffiliateEntityKind;
  entity_id: string;
  trip_id: string;
  context?: string;
};

export interface AffiliateClickEvent {
  name: 'affiliate_click';
  properties: AffiliateEventProperties;
}

export interface AffiliateImpressionEvent {
  name: 'affiliate_impression';
  properties: AffiliateEventProperties;
}

function toProperties(input: BuildAffiliateEventInput): AffiliateEventProperties {
  const properties: AffiliateEventProperties = {
    provider: input.provider,
    surface: input.surface,
    product: input.product,
    entity_kind: input.entityKind,
    entity_id: input.entityId,
    trip_id: input.tripId,
  };
  if (input.context) properties.context = input.context;
  return properties;
}

export function buildAffiliateClickEvent(
  input: BuildAffiliateEventInput,
): AffiliateClickEvent {
  return { name: 'affiliate_click', properties: toProperties(input) };
}

export function buildAffiliateImpressionEvent(
  input: BuildAffiliateEventInput,
): AffiliateImpressionEvent {
  return { name: 'affiliate_impression', properties: toProperties(input) };
}
